import React from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Person as PersonIcon } from '@mui/icons-material';

import { commonButtonStyles } from '../../../components/styles/common-style';
import { DepartmentResponse, Staff } from '../types';

import { NoharaRepresentative } from '@/features/companies/types/company.types';
import { DISABLED_DEPARTMENT_CODE } from '@/lib/constants';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface RepresentativesSectionProps {
  readonly representatives: NoharaRepresentative[];
  readonly originalRepresentative?: NoharaRepresentative[];
  readonly isEditMode?: boolean;
  readonly onAddRepresentative: () => void;
  readonly onRemoveRepresentative: (id: string) => void;
  readonly onUpdateRepresentative: (
    id: string,
    field: keyof NoharaRepresentative,
    value: NoharaRepresentative[keyof NoharaRepresentative]
  ) => void;
  readonly getSalesPersonOptions: (departmentId: string) => Staff[];
  readonly getFieldError: (fieldName: string) => string | undefined;
  readonly isFieldValid: (fieldName: string) => boolean;
  readonly canAddRepresentative: boolean;
  readonly departments: DepartmentResponse[];
  readonly departmentsLoading: boolean;
  readonly staffsLoading: boolean;
  readonly onDepartmentChange: (departmentId: string) => void;
}

interface RepresentativeCardProps {
  readonly representative: NoharaRepresentative;
  readonly originalRepresentative?: NoharaRepresentative;
  readonly isEditMode?: boolean;
  readonly index: number; // Display index (for UI)
  readonly originalIndex: number; // Original index in formData.representatives (for validation)
  readonly onUpdate: (
    field: keyof NoharaRepresentative,
    value: NoharaRepresentative[keyof NoharaRepresentative]
  ) => void;
  readonly onRemove: () => void;
  readonly getSalesPersonOptions: (departmentId: string) => Staff[];
  readonly getFieldError: (fieldName: string) => string | undefined;
  readonly isFieldValid: (fieldName: string) => boolean;
  readonly departments: DepartmentResponse[];
  readonly departmentsLoading: boolean;
  readonly staffsLoading: boolean;
  readonly onDepartmentChange: (departmentId: string) => void;
  readonly canRemoveRepresentative: boolean;
}

const RepresentativeCard: React.FC<RepresentativeCardProps> = ({
  representative,
  originalRepresentative,
  isEditMode,
  index,
  originalIndex,
  onUpdate,
  onRemove,
  getSalesPersonOptions,
  getFieldError,
  isFieldValid,
  departments,
  departmentsLoading,
  staffsLoading,
  onDepartmentChange,
  canRemoveRepresentative = true,
}) => {
  const salesPersonOptions = getSalesPersonOptions(representative.department);
  const isSalesPersonDisabled = !representative.department;
  const renderSalesPersonSelect = (selectId: string) => {
    if (!salesPersonOptions) {
      return `${representative.salesPerson.lastName} ${representative.salesPerson.firstName}`;
    }
    const selectedStaff = salesPersonOptions.find((staff) => staff.id === selectId);
    if (selectedStaff) {
      return `${selectedStaff.lastName} ${selectedStaff.firstName}`;
    }
    return `${representative.salesPerson.lastName} ${representative.salesPerson.firstName}`;
  };
  const updateSalesPerson = (staffId: string) => {
    if (!salesPersonOptions) {
      onUpdate('salesPerson', { id: '', firstName: '', lastName: '', code: '' });
      return;
    }
    const selectedStaff = salesPersonOptions.find((staff) => staff.id === staffId);
    if (selectedStaff) {
      onUpdate('salesPerson', {
        id: selectedStaff.id,
        firstName: selectedStaff.firstName,
        lastName: selectedStaff.lastName,
        code: selectedStaff.code,
      });
    } else {
      onUpdate('salesPerson', { id: '', firstName: '', lastName: '', code: '' });
    }
  };
  const renderDepartmentSelect = (selectedId: string) => {
    const selectedDepartment = departments.find((dept) => dept.id === selectedId);
    const departmentName = selectedDepartment?.name || representative.departmentName;
    return departmentName || '';
  };
  const isDepartmentDisabled = representative.canUpdate === false;
  return (
    <Card variant='outlined' sx={{ mb: 2, position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#0047BA' }} />
            <Typography variant='subtitle1' fontWeight='bold'>
              担当 #{index + 1}
            </Typography>
          </Box>
          {!isDepartmentDisabled && canRemoveRepresentative && (
            <IconButton
              onClick={onRemove}
              size='small'
              sx={{ color: '#FF8985' }}
              aria-label={`担当者を削除 ${index + 1}`}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Department Selection */}
          <FormControl
            fullWidth
            required
            error={!isFieldValid(`representatives.${originalIndex}.department`)}
          >
            <InputLabel>担当部署</InputLabel>
            <Select
              value={representative.department}
              onChange={(e) => {
                const departmentId = e.target.value;
                onUpdate('department', departmentId);
                // Load staffs for the new department
                onDepartmentChange(departmentId);
                // Note: salesPerson is automatically cleared in updateRepresentative when department changes
              }}
              label='担当部署'
              renderValue={(selectedId) => {
                return renderDepartmentSelect(selectedId);
              }}
              disabled={departmentsLoading || isDepartmentDisabled}
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalRepresentative?.department,
                    representative.department
                  ),
                },
              }}
            >
              {departments?.map((dept) => (
                <MenuItem
                  key={dept.id}
                  value={dept.id}
                  disabled={dept.code === DISABLED_DEPARTMENT_CODE}
                >
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            {getFieldError(`representatives.${originalIndex}.department`) && (
              <Typography variant='caption' color='error' sx={{ mt: 1, ml: 2 }}>
                {getFieldError(`representatives.${originalIndex}.department`)}
              </Typography>
            )}
          </FormControl>

          {/* Sales Person Selection */}
          <FormControl fullWidth required disabled={isSalesPersonDisabled || staffsLoading}>
            <InputLabel>営業担当者</InputLabel>
            <Select
              value={representative.salesPerson.id}
              onChange={(e) => updateSalesPerson(e.target.value)}
              label='営業担当者'
              renderValue={(selectedId) => {
                return renderSalesPersonSelect(selectedId);
              }}
              sx={{
                '& .MuiSelect-select': {
                  maxWidth: '600px',
                },
              }}
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalRepresentative?.salesPerson.id,
                    representative.salesPerson.id
                  ),
                },
              }}
              disabled={isDepartmentDisabled}
            >
              {salesPersonOptions === null ||
              salesPersonOptions === undefined ||
              salesPersonOptions.length === 0 ? (
                <MenuItem value='' disabled>
                  {staffsLoading ? '読み込み中...' : '担当者がいません'}
                </MenuItem>
              ) : (
                salesPersonOptions.map((staff) => (
                  <MenuItem key={staff.id} value={staff.id}>
                    {`${staff.lastName} ${staff.firstName}`}
                  </MenuItem>
                ))
              )}
            </Select>
            {getFieldError(`representatives.${originalIndex}.salesPerson`) && (
              <Typography variant='caption' color='error' sx={{ mt: 1, ml: 2 }}>
                {getFieldError(`representatives.${originalIndex}.salesPerson`)}
              </Typography>
            )}
            {isSalesPersonDisabled && (
              <Typography variant='caption' color='textSecondary' sx={{ mt: 1 }}>
                まず担当部署を選択してください
              </Typography>
            )}
            {staffsLoading && (
              <Typography variant='caption' color='textSecondary' sx={{ mt: 1 }}>
                営業担当者を読み込み中...
              </Typography>
            )}
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
};

export const RepresentativesSection: React.FC<RepresentativesSectionProps> = ({
  representatives,
  originalRepresentative,
  isEditMode,
  onAddRepresentative,
  onRemoveRepresentative,
  onUpdateRepresentative,
  getSalesPersonOptions,
  getFieldError,
  isFieldValid,
  canAddRepresentative,
  departments,
  departmentsLoading,
  staffsLoading,
  onDepartmentChange,
}) => {
  // Filter representatives to only show those with canUpdate: true or canUpdate === undefined (new records)
  const visibleRepresentatives = representatives.filter(
    (rep) => rep.canView === true || rep.canView === undefined
  );
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '40px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography
          variant='h5'
          sx={{
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
          }}
        >
          野原担当
        </Typography>
      </Box>

      {/* Max representatives warning */}
      {visibleRepresentatives.length >= 10 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          野原担当者は最大10名まで登録可能です。
        </Alert>
      )}

      {/* Representatives List */}
      {visibleRepresentatives.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <PersonIcon sx={{ fontSize: 48, mb: 1, color: '#0047BA' }} />
          <Typography variant='body1'>野原担当</Typography>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={onAddRepresentative}
            disabled={!canAddRepresentative}
            sx={{
              ...commonButtonStyles,
              mt: 2,
              px: 3,
              py: 1,
            }}
          >
            入力行を追加
          </Button>
        </Box>
      ) : (
        <Box>
          {representatives.map((rep, filteredIndex) => {
            if (!(rep.canView === true || rep.canView === undefined)) return null;
            const originalIndex = filteredIndex;
            // Use unique ID if available, otherwise fallback to originalIndex + filteredIndex combination
            // This ensures uniqueness even when representatives are empty (same department and salesPerson.id)
            const uniqueKey = rep.id || `rep-${originalIndex}-${filteredIndex}`;
            const originalRepresentativeItem =
              rep.originalIndex !== undefined
                ? originalRepresentative?.[rep.originalIndex]
                : undefined;
            return (
              <RepresentativeCard
                key={`representative-${uniqueKey}`}
                representative={rep}
                originalRepresentative={originalRepresentativeItem}
                isEditMode={isEditMode}
                index={filteredIndex}
                originalIndex={originalIndex}
                onUpdate={(field, value) =>
                  onUpdateRepresentative(originalIndex.toString(), field, value)
                }
                onRemove={() => onRemoveRepresentative(originalIndex.toString())}
                getSalesPersonOptions={getSalesPersonOptions}
                getFieldError={getFieldError}
                isFieldValid={isFieldValid}
                departments={departments}
                departmentsLoading={departmentsLoading}
                staffsLoading={staffsLoading}
                onDepartmentChange={onDepartmentChange}
                canRemoveRepresentative={representatives.length > 1}
              />
            );
          })}
        </Box>
      )}

      {/* Add Representative Button (bottom) */}
      {visibleRepresentatives.length > 0 && canAddRepresentative && (
        <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={onAddRepresentative}
            sx={{
              ...commonButtonStyles,
              px: 3,
              py: 1,
            }}
          >
            入力行を追加 ({visibleRepresentatives.length}/10)
          </Button>
        </Box>
      )}
    </PaperBoxStyles>
  );
};
